import { Avatar, Button, ListItem, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { Dispatch, useCallback, useLayoutEffect, useState } from 'react';
import { PurpleX } from 'Images';
import { useDictionary, useIsDesktop } from 'Hooks';
import { ToggleableChip } from './ToggleableChip';
import { RevealFiltersButton } from './RevealFiltersButton';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';

const containerMaxWidth = 850;

export interface IFilterTag {
  label: string;
  isActive: boolean;
}
interface IFilterTags {
  reset: () => void;
  subjects: string[];
  selectedSubjects: string[];
  setSelectedSubjects: Dispatch<React.SetStateAction<string[]>>;
  clearSelectedSubjects: () => void;
}
export const FilterTags = ({
  subjects,
  selectedSubjects,
  setSelectedSubjects,
  reset,
  clearSelectedSubjects,
}: IFilterTags) => {
  const [isMoreThan1Line, setIsMoreThan1Line] = useState(false);
  const [isTagsRevealed, setIsTagsRevealed] = useState(false);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Documents);
  const isDesktop = useIsDesktop();

  const getTextWidth = useCallback((text: string) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    let width = 0;
    const minWidth = 64;

    if (context) {
      context.font = getComputedStyle(document.body).font;
      width = context.measureText(text).width;
    }

    canvas.remove();
    if (width < minWidth) return minWidth;
    return width;
  }, []);

  const toggleSelectedSubject = (subject: string) => {
    setSelectedSubjects((prevSelectedSubjects) => {
      if (prevSelectedSubjects.includes(subject)) {
        return prevSelectedSubjects.filter(
          (selectedSubject) => selectedSubject !== subject
        );
      } else {
        return [...prevSelectedSubjects, subject];
      }
    });
  };

  const isChipActive = useCallback(
    (title: string): boolean => {
      return selectedSubjects.includes(title);
    },
    [selectedSubjects]
  );

  useLayoutEffect(() => {
    const tagWidths: number[] = subjects.map(getTextWidth);
    const tagWidthsSum = tagWidths.reduce((sum, width) => (sum += width), 0);
    const isOverflowing = tagWidthsSum > containerMaxWidth;

    setIsMoreThan1Line(isOverflowing);
  }, [getTextWidth, isMoreThan1Line, subjects]);

  const expandFilterTags = () => {
    setIsTagsRevealed(true);
  };

  const collapseFilterTags = () => {
    setIsTagsRevealed(false);
    clearSelectedSubjects();
  };

  return (
    <Stack gap={isDesktop ? '24px' : '16px'}>
      <Typography variant='body1' color='text.secondary' component='h2'>
        {dictionary.filterTitle}
      </Typography>
      <Stack direction={'column'} gap={isDesktop ? '24px' : '16px'}>
        <Stack direction={'row'} maxWidth={'920px'}>
          <Stack
            direction={'row'}
            component={'ul'}
            padding={0}
            margin={0}
            gap={'8px'}
            overflow={'hidden'}
            height={isTagsRevealed ? 'auto' : '40px'}
            flexWrap={'wrap'}
          >
            {subjects.map((title, index) => (
              <ListItem
                key={index}
                sx={{
                  cursor: 'pointer',
                  display: 'inline',
                  width: 'auto',
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                value={title}
              >
                <ToggleableChip
                  chip={{ label: title, isActive: isChipActive(title) }}
                  onClick={() => toggleSelectedSubject(title)}
                />
              </ListItem>
            ))}
            {isMoreThan1Line && isTagsRevealed && (
              <RevealFiltersButton
                isExpanded={true}
                onClick={() => collapseFilterTags()}
              />
            )}
          </Stack>
          {isMoreThan1Line && !isTagsRevealed && (
            <RevealFiltersButton
              isExpanded={false}
              onClick={() => expandFilterTags()}
            />
          )}
        </Stack>
        {selectedSubjects.length > 0 && (
          <Button
            variant='textBtn'
            onClick={reset}
            onKeyDown={onEnterOrEscapeKeyDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              textDecoration: 'none',
              '&:hover': {
                background: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            <Avatar
              sx={{ width: '22px', height: '22px' }}
              src={PurpleX}
              alt=''
            />
            <Typography variant='body2'>{dictionary.filterReset}</Typography>
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
